import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";
import { useState, useEffect } from "react";

interface CampaignDateFilterProps {
  dateRange?: { from?: Date; to?: Date };
  onDateRangeChange: (range: { from?: Date; to?: Date } | undefined) => void;
}

export function CampaignDateFilter({
  dateRange,
  onDateRangeChange,
}: CampaignDateFilterProps) {
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange | undefined>(dateRange as DateRange);
  const hasActiveFilter = !!(dateRange?.from || dateRange?.to);

  // Sincronizar tempRange quando o popover abrir
  useEffect(() => {
    if (open) {
      setTempRange(dateRange as DateRange);
    }
  }, [open, dateRange]);

  const handleClear = () => {
    setTempRange(undefined);
    onDateRangeChange(undefined);
    setOpen(false);
  };

  const handleApply = () => {
    if (tempRange?.from) {
      // Se só selecionou uma data (from), usar mesma data para from e to
      const finalRange = {
        from: tempRange.from,
        to: tempRange.to || tempRange.from,
      };
      onDateRangeChange(finalRange);
    } else {
      onDateRangeChange(undefined);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 border-dashed",
            !dateRange && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateRange?.from ? (
            dateRange.to ? (
              <>
                {format(dateRange.from, "dd/MM/yy", { locale: ptBR })} -{" "}
                {format(dateRange.to, "dd/MM/yy", { locale: ptBR })}
              </>
            ) : (
              format(dateRange.from, "dd/MM/yy", { locale: ptBR })
            )
          ) : (
            <span>Período</span>
          )}
          {hasActiveFilter && (
            <>
              <div className="mx-2 h-4 w-[1px] bg-gray-300" />
              <X
                className="h-3 w-3"
                onClick={(e) => {
                  e.stopPropagation();
                  onDateRangeChange(undefined);
                }}
              />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={tempRange}
          onSelect={setTempRange}
          locale={ptBR}
          numberOfMonths={2}
        />
        <div className="border-t p-3">
          <div className="flex justify-between items-center gap-2">
            <p className="text-xs text-muted-foreground">
              {tempRange?.from && !tempRange?.to
                ? "Selecione a data final ou clique em Aplicar para usar o mesmo dia"
                : "Selecione o período"}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs"
                onClick={handleClear}
              >
                Limpar
              </Button>
              <Button
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={handleApply}
                disabled={!tempRange?.from}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
